<template>
  <div
    v-if="appointmentData"
    class="popover-container w-full min-w-0 bg-primary text-primary"
  >
    <div class="flex min-w-0">
      <!-- Status stripe for quick scanning without a heavy colour block -->
      <div
        class="w-1 shrink-0 self-stretch"
        :style="statusStripeStyle"
      />

      <div class="flex-1 min-w-0">
        <div class="p-3.5 flex flex-col gap-3">
          <div class="flex justify-between gap-3 items-start min-w-0">
            <Chip
              :label="statusLabel"
              :severity="statusChipSeverity"
              size="sm"
              :rounded="false"
              class="shrink-0"
            />
            <Chip
              icon="user"
              :rounded="false"
              size="sm"
              class="min-w-0 max-w-[50%]"
            >
              <span class="block min-w-0 truncate">{{ appointmentData.assignedToName || $t('ui.entities.appointment.status.unassigned') }}</span>
            </Chip>
          </div>

          <div class="min-w-0">
            <div class="text-base font-semibold truncate">
              {{ appointmentData.customerName }}
            </div>
            <div class="text-sm text-secondary mt-0.5">
              {{ formattedTime }}
            </div>
          </div>

          <div class="flex flex-col gap-1.5 text-sm text-secondary">
            <div class="flex gap-2 items-center min-w-0">
              <Icon
                type="location-dot"
                class="shrink-0"
              />
              <span class="truncate">{{ appointmentData.dealershipName }}</span>
            </div>
            <div class="flex gap-2 items-center min-w-0">
              <Icon
                :type="methodIcon"
                class="shrink-0"
              />
              <span class="truncate">{{ methodLabel }}</span>
            </div>
            <div class="flex gap-2 items-center min-w-0">
              <Icon
                :type="appointmentData.customerConfirmed ? 'user-check' : 'user-xmark'"
                class="shrink-0"
              />
              <span>
                {{ appointmentData.customerConfirmed ? 'Customer confirmed' : 'Not confirmed' }}
              </span>
            </div>
          </div>

          <!-- Notes are clamped so the hover card stays scannable -->
          <div
            v-if="appointmentData.note"
            class="border-t border-secondary pt-3"
          >
            <div class="text-sm text-secondary leading-snug line-clamp-3 break-words">
              {{ appointmentData.note }}
            </div>
          </div>
        </div>

        <!-- Soft outcome panel: only when there is something beyond the status chip -->
        <div
          v-if="showOutcomePanel"
          class="px-3.5 py-3 border-t border-secondary"
          :style="outcomePanelStyle"
        >
          <div
            v-if="showOutcomeLabel"
            class="text-sm font-semibold"
          >
            {{ outcomeLabel }}
          </div>
          <div
            v-if="appointmentData.outcomeReason"
            class="text-sm leading-snug line-clamp-2 break-words"
            :class="{ 'mt-1': showOutcomeLabel }"
          >
            {{ appointmentData.outcomeReason }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

// components
import Chip from '@/components/chip/Chip.vue'
import Icon from '@/components/icon/Icon.vue'

// dictionary
import { getDictionaryItem } from '@/dictionary'

// utilities
import { getAppointmentStatusTheme } from '@/utilities/appointments/calendarEvents'

// types
import type { CalendarAppointmentExtendedProps } from '@/utilities/appointments/calendarEvents'

/**
 * Soft surface tokens for the outcome panel, keyed by appointment status.
 */
interface OutcomeSurfaceTheme {
  background: string
  color: string
}

/** Chip severities supported by the shared Chip component. */
type ChipSeverity = 'default' | 'brand' | 'warning' | 'error' | 'danger' | 'success' | 'info'

const props = defineProps<{
  /** Appointment fields shown in the shared calendar hover popover */
  appointmentData: CalendarAppointmentExtendedProps | null
}>()

const { t } = useI18n()

/**
 * Soft tinted surfaces for outcome panels by status.
 * Prefer quiet backgrounds over solid colour blocks on a hover preview.
 */
const OUTCOME_SURFACE_BY_STATUS: Record<string, OutcomeSurfaceTheme> = {
  unassigned: {
    background: 'var(--utility-gray-50)',
    color: 'var(--text-secondary)'
  },
  assigned: {
    background: 'var(--utility-blue-50)',
    color: 'var(--utility-blue-900)'
  },
  cancelled: {
    background: 'var(--utility-error-50)',
    color: 'var(--text-error)'
  },
  'no-show': {
    background: 'var(--utility-error-50)',
    color: 'var(--text-error)'
  },
  completed: {
    background: 'var(--utility-success-50)',
    color: 'var(--text-success)'
  },
  ongoing: {
    background: 'var(--utility-purple-50)',
    color: 'var(--utility-purple-700)'
  }
}

/**
 * Colour tokens for the currently hovered appointment status.
 */
const themeColors = computed(() => getAppointmentStatusTheme(props.appointmentData?.status ?? ''))

/**
 * Left status stripe style.
 */
const statusStripeStyle = computed(() => ({
  backgroundColor: `var(--${themeColors.value.statusColor})`
}))

/**
 * Soft outcome panel colours derived from status.
 */
const outcomePanelStyle = computed(() => {
  const surface = OUTCOME_SURFACE_BY_STATUS[props.appointmentData?.status ?? ''] ?? OUTCOME_SURFACE_BY_STATUS.unassigned

  return {
    backgroundColor: surface.background,
    color: surface.color
  }
})

/**
 * Localised appointment status label.
 */
const statusLabel = computed(() => {
  const localeKey = getDictionaryItem('appointmentStatus', props.appointmentData?.status ?? '')?.localeKey

  // Fall back to the raw status when the dictionary entry is missing
  if (!localeKey) {
    return props.appointmentData?.status ?? ''
  }

  return t(localeKey)
})

/**
 * Chip severity mapped from the appointment status dictionary colour.
 */
const statusChipSeverity = computed((): ChipSeverity => {
  const color = getDictionaryItem('appointmentStatus', props.appointmentData?.status ?? '')?.color

  // Map dictionary colours onto Chip severities; default keeps unknown statuses neutral
  switch (color) {
    case 'brand':
      return 'brand'
    case 'warning':
      return 'warning'
    case 'error':
      return 'error'
    case 'success':
      return 'success'
    case 'info':
      return 'info'
    default:
      return 'default'
  }
})

/**
 * Appointment time formatted for the popover header.
 */
const formattedTime = computed(() => {
  // Skip formatting when there is no appointment payload yet
  if (!props.appointmentData?.time) {
    return ''
  }

  return dayjs(props.appointmentData.time).format('HH:mm')
})

/**
 * Method icon from the appointment method dictionary.
 */
const methodIcon = computed(() => {
  return getDictionaryItem('appointmentMethod', props.appointmentData?.method ?? '')?.icon ?? 'phone'
})

/**
 * Localised method label.
 */
const methodLabel = computed(() => {
  const localeKey = getDictionaryItem('appointmentMethod', props.appointmentData?.method ?? '')?.localeKey

  // Fall back to the raw method key when localisation is unavailable
  if (!localeKey) {
    return props.appointmentData?.method ?? ''
  }

  return t(localeKey)
})

/**
 * Localised outcome label when an outcome is present.
 */
const outcomeLabel = computed(() => {
  // Hide the label entirely when there is no outcome
  if (!props.appointmentData?.outcome) {
    return ''
  }

  const localeKey = getDictionaryItem('appointmentOutcome', props.appointmentData.outcome)?.localeKey

  // Fall back to the raw outcome key when localisation is unavailable
  if (!localeKey) {
    return props.appointmentData.outcome
  }

  return t(localeKey)
})

/**
 * Whether the outcome name adds information beyond the status chip.
 * Avoids repeating "No-Show" when status and outcome are the same concept.
 */
const showOutcomeLabel = computed(() => {
  const outcome = props.appointmentData?.outcome
  const status = props.appointmentData?.status

  // Only show the outcome title when it differs from the status already shown above
  return Boolean(outcome && outcome !== status)
})

/**
 * Whether to render the soft outcome panel.
 * Show when there is a distinct outcome label or an outcome reason to read.
 */
const showOutcomePanel = computed(() => {
  return showOutcomeLabel.value || Boolean(props.appointmentData?.outcomeReason)
})
</script>

<style>
.appointment-hover-popover .popover-container {
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  border-radius: var(--rounded-lg);
  border: 1px solid var(--border-secondary);
}
</style>
